import { useEffect, useRef, useState } from "react";
import tokenService from "services/token.service";
import { useAccount } from "wagmi";
import { useLogin } from "./useLogin";
import { setAuthInProgress } from "./useAuth";
import { useTokenValidation } from "./useTokenValidation";

const MAX_AUTO_LOGIN_ATTEMPTS = 2;
const AUTO_LOGIN_RETRY_DELAY = 3000;

// Global flag to track manual disconnection
let userManuallyDisconnected = false;

export const setUserManuallyDisconnected = (value: boolean) => {
  userManuallyDisconnected = value;
};

export const useConnect = () => {
  const { address } = useAccount();
  const [isAttemptingLogin, setIsAttemptingLogin] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const hasLoggedIn = useRef(false);
  const loginAttempts = useRef(0);
  const lastAttemptAddress = useRef<string | null>(null);
  const userRejectedLogin = useRef<Set<string>>(new Set());
  const tokenValidated = useRef(false);
  const nextAutoLoginAt = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { loginMutation, isMessageSigning, isLoading, error } = useLogin();
  const { validateCurrentToken } = useTokenValidation();

  useEffect(() => {
    const loginUser = async () => {
      if (!address || isAttemptingLogin || hasLoggedIn.current) {
        return;
      }

      // Don't attempt login if user manually disconnected
      if (userManuallyDisconnected) {
        console.log('[useConnect] Skipping auto-login - user manually disconnected');
        return;
      }

      // Check if user previously rejected login for this address
      if (userRejectedLogin.current.has(address.toLowerCase())) {
        console.log('[useConnect] User previously rejected login for', address);
        return;
      }

      // Prevent infinite login loops while still allowing one transient retry.
      if (
        lastAttemptAddress.current === address &&
        loginAttempts.current >= MAX_AUTO_LOGIN_ATTEMPTS
      ) {
        console.log('[useConnect] Max login attempts reached for', address);
        return;
      }

      if (nextAutoLoginAt.current > Date.now()) {
        return;
      }

      // Check if user already has valid tokens for this address
      const existingTokens = tokenService.tokens;
      const existingAddress = existingTokens?.user?.address?.toLowerCase();
      const currentAddress = address.toLowerCase();

      console.log('[useConnect] Checking existing tokens:', {
        hasExistingTokens: !!existingTokens,
        existingAddress,
        currentAddress,
        addressesMatch: existingAddress === currentAddress,
        loginAttempts: loginAttempts.current,
      });

      // If token exists but for different address, clear it
      if (existingTokens && existingAddress && existingAddress !== currentAddress) {
        console.log('[useConnect] Clearing tokens for old address:', existingAddress);
        tokenService.removeToken();
        tokenValidated.current = false;
      }

      // If token exists for current address, validate it
      if (existingTokens && existingAddress === currentAddress) {
        // Only validate once per session
        if (!tokenValidated.current) {
          console.log('[useConnect] Validating existing token for', address);
          const isValid = await validateCurrentToken();

          if (isValid) {
            console.log('[useConnect] Token is valid, using existing session');
            hasLoggedIn.current = true;
            tokenValidated.current = true;
            return;
          } else {
            console.log('[useConnect] Token validation failed, will request new login');
            tokenService.removeToken();
            tokenValidated.current = false;
            // Continue to login flow below
          }
        } else {
          console.log('[useConnect] Token already validated, using existing session');
          hasLoggedIn.current = true;
          return;
        }
      }

      // Track login attempt
      if (lastAttemptAddress.current !== address) {
        lastAttemptAddress.current = address;
        loginAttempts.current = 0;
      }
      loginAttempts.current++;

      // Set both local and global auth in progress flags
      console.log('[useConnect] No valid tokens found, initiating login for', address, '(attempt', loginAttempts.current, ')');
      setIsAttemptingLogin(true);
      setAuthInProgress(true);

      try {
        const result = await loginMutation(address);
        if (result.success) {
          console.log('[useConnect] Login successful for', address);
          hasLoggedIn.current = true;
          loginAttempts.current = 0;
          // Remove from rejected list if login succeeds
          userRejectedLogin.current.delete(address.toLowerCase());
        } else {
          console.warn('[useConnect] Login failed for', address, {
            errorType: result.errorType,
            message: result.message,
          });

          // Only suppress automatic retries for wallet-side pending/rejected flows.
          if (
            result.errorType === "user_rejected" ||
            result.errorType === "request_pending"
          ) {
            userRejectedLogin.current.add(address.toLowerCase());
          } else if (loginAttempts.current < MAX_AUTO_LOGIN_ATTEMPTS) {
            nextAutoLoginAt.current = Date.now() + AUTO_LOGIN_RETRY_DELAY;

            if (retryTimerRef.current) {
              clearTimeout(retryTimerRef.current);
            }

            retryTimerRef.current = setTimeout(() => {
              retryTimerRef.current = null;
              setRetryTick((tick) => tick + 1);
            }, AUTO_LOGIN_RETRY_DELAY);
          }
        }
      } catch (err: any) {
        console.error("[useConnect] Login error:", err);

        // Check if user rejected the request
        const isUserRejection = err?.message?.includes('User rejected') ||
                               err?.message?.includes('user rejected') ||
                               err?.code === 4001 ||
                               err?.name === 'UserRejectedRequestError';

        if (isUserRejection) {
          console.log('[useConnect] User rejected login request, will not retry');
          userRejectedLogin.current.add(address.toLowerCase());
        }
      } finally {
        setIsAttemptingLogin(false);
        setAuthInProgress(false);
      }
    };

    loginUser();
  }, [address, loginMutation, isAttemptingLogin, retryTick]);

  // Reset login state when address changes
  useEffect(() => {
    console.log('[useConnect] Address changed to:', address);
    hasLoggedIn.current = false;
    setIsAttemptingLogin(false);
    setAuthInProgress(false);

    // Reset login attempts only if address actually changed
    if (lastAttemptAddress.current !== address) {
      console.log('[useConnect] Resetting login attempts for new address');
      loginAttempts.current = 0;
      lastAttemptAddress.current = null;
      tokenValidated.current = false; // Reset validation flag on address change
      nextAutoLoginAt.current = 0;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      // Note: we don't clear userRejectedLogin here - user must manually retry if they rejected
    }
  }, [address]);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  return {
    isLoginMessageSigning: isMessageSigning,
    isLoading,
    error,
  };
};
